import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { resolveImageUrl } from "../utils/imageUrl";
import { Loader2, ChevronDown, ChevronUp } from "lucide-react";

export default function InteractiveCategoryDesign({ categoryId, categories }) {
    const [design, setDesign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hoveredPin, setHoveredPin] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [manualToggle, setManualToggle] = useState(false);
    const containerRef = useRef(null);
    const lastScrollY = useRef(0);
    const scrollThreshold = 120;

    useEffect(() => {
        if (!categoryId) { setLoading(false); return; }
        api.get(`/category-design/by-category/${categoryId}`)
            .then(r => setDesign(r.data))
            .catch(() => setDesign(null))
            .finally(() => setLoading(false));
    }, [categoryId]);

    const handleScroll = useCallback(() => {
        if (manualToggle) return;
        const currentY = window.scrollY;
        const delta = currentY - lastScrollY.current;
        if (delta > scrollThreshold && currentY > 200) {
            setCollapsed(true);
        } else if (delta < -scrollThreshold) {
            setCollapsed(false);
        }
        lastScrollY.current = currentY;
    }, [manualToggle]);

    useEffect(() => {
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [handleScroll]);

    const toggleManual = () => {
        setManualToggle(true);
        setCollapsed(prev => !prev);
        setTimeout(() => setManualToggle(false), 2000);
    };

    if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-green-600" /></div>;
    if (!design || !design.background_image) return null;

    const getCategorySlug = (subcatId) => {
        const cat = categories?.find(c => c.id === subcatId);
        return cat?.slug || subcatId;
    };

    return (
        <div ref={containerRef} className="relative mb-6" data-testid="interactive-design">
            {/* Collapsible wrapper */}
            <div
                className="rounded-2xl overflow-hidden shadow-lg bg-white transition-all duration-700 ease-in-out"
                style={{
                    maxHeight: collapsed ? "56px" : "900px",
                    opacity: collapsed ? 0.85 : 1,
                }}
            >
                {/* Header - always visible */}
                <button
                    onClick={toggleManual}
                    className="w-full flex items-center justify-between px-5 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                    data-testid="design-toggle-btn"
                >
                    <h2 className="text-lg font-heading font-bold text-gray-900">
                        {design.title || "Interaktivni mapa"}
                    </h2>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400 font-medium">
                            {collapsed ? "Zobrazit mapu" : "Schovat"}
                        </span>
                        <div className={`transition-transform duration-500 ${collapsed ? "rotate-0" : "rotate-180"}`}>
                            <ChevronDown size={16} className="text-gray-400" />
                        </div>
                    </div>
                </button>

                {/* Image + Pins */}
                <div className={`relative transition-all duration-700 ease-in-out ${collapsed ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
                    <img
                        src={resolveImageUrl(design.background_image)}
                        alt={design.title || "Interaktivni kategorie"}
                        className="w-full h-auto block"
                        draggable={false}
                    />
                    {!collapsed && (design.pins || []).map(pin => (
                        <Link
                            key={pin.pin_id}
                            to={`/kategorie/${getCategorySlug(pin.subcategory_id)}`}
                            className="absolute group"
                            style={{ left: `${pin.x}%`, top: `${pin.y}%`, transform: "translate(-50%, -50%)" }}
                            onMouseEnter={() => setHoveredPin(pin.pin_id)}
                            onMouseLeave={() => setHoveredPin(null)}
                            data-testid={`design-pin-${pin.pin_id}`}
                        >
                            {/* Pin - pulsing circle with configurable color */}
                            <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all duration-300 ${hoveredPin === pin.pin_id ? "scale-125" : ""}`}>
                                {pin.pulse !== false && (
                                    <div className="absolute inset-0 w-11 h-11 sm:w-14 sm:h-14 rounded-full border-2 animate-ping pointer-events-none"
                                        style={{ animationDuration: "2s", borderColor: `${pin.pulse_color || "#ffffff"}70` }} />
                                )}
                                <div className={`absolute inset-0 w-11 h-11 sm:w-14 sm:h-14 rounded-full border-2 transition-all duration-300 ${hoveredPin === pin.pin_id ? "shadow-[0_0_20px_rgba(255,255,255,0.6)]" : ""}`}
                                    style={{
                                        borderColor: hoveredPin === pin.pin_id ? (pin.pulse_color || "#ffffff") : `${pin.pulse_color || "#ffffff"}60`,
                                        backgroundColor: hoveredPin === pin.pin_id ? `${pin.pulse_color || "#ffffff"}25` : "transparent",
                                    }} />
                            </div>

                            {/* Tooltip */}
                            <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-3 py-2 rounded-xl shadow-xl whitespace-nowrap transition-all duration-200 pointer-events-none ${hoveredPin === pin.pin_id ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"} bg-white border border-gray-200`}>
                                <p className="text-sm font-semibold text-gray-900">{pin.label || pin.subcategory_name}</p>
                                {pin.product_count != null && (
                                    <p className="text-[11px] text-green-600 font-medium">{pin.product_count} produktu</p>
                                )}
                                <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-b border-r border-gray-200 rotate-45 -mt-1" />
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
